import type { SerialRowMap } from './cartTypes';
import { cartItemKey } from './cartItemKey';
import { getBasePrice, getEffectivePrice } from './cartPricing';
import { isUpgradeItem } from './cartLineKinds';

export interface DiscountDescriptor {
  key: string;
  text: string;
  // Coupon savings attributed to the SUMMARY Discount row (face value vs list
  // for copies the discount won; never the leftover vs sale).
  amount?: number;
}

type CopyLimitScope = 'item' | 'order';

export const getCopyLimitScope = (d: any): CopyLimitScope =>
  d?.copyLimitScope === 'order' ? 'order' : 'item';

/** True when the line's discount applies to the order total, not the line. */
export const isOrderScopeDiscount = (d: any): boolean =>
  !!d && getCopyLimitScope(d) === 'order';

type LineBreakdown = {
  /** Pre-discount subtotal contribution (list for won copies, sale otherwise). */
  base: number;
  /** What the customer pays for this line. */
  charge: number;
  /** Face coupon savings vs list on copies the discount won. */
  discount: number;
  wonCopies: number;
};

/**
 * Per-copy choose-min for ITEM-scope discounts (matches BE getSubtotal):
 * - Every copy gets the discount (quantityAvailable is a global redemption
 *   pool, not a per-cart cap)
 * - Discounted copy pays min(sale, list−coupon)
 * - SUMMARY Discount only counts face value when the coupon wins that copy
 * - When sale wins every discounted copy → discount 0, base stays sale-aware
 */
function breakdownDiscountedLine(it: any): LineBreakdown {
  const d = it.discountId;
  const listUnit = getBasePrice(it.productId, it.hostApplication);
  const saleAwareUnit = getEffectivePrice(it.productId, it.hostApplication);
  const qty = Number(it.quantity) || 0;

  if (!d || qty <= 0) {
    const base = saleAwareUnit * qty;
    return { base, charge: base, discount: 0, wonCopies: 0 };
  }

  let unitOffList: number | null = null;
  if (d.amountUnits === 'dollars') {
    unitOffList = Number(d.amount) || 0;
  } else if (d.amountUnits === 'percent') {
    unitOffList = listUnit * (Number(d.amount) / 100);
  }

  if (unitOffList == null) {
    const base = saleAwareUnit * qty;
    return { base, charge: base, discount: 0, wonCopies: 0 };
  }

  const discountedListUnit = Math.max(0, listUnit - unitOffList);
  const discountWon = discountedListUnit <= saleAwareUnit;

  if (!discountWon) {
    // Sale beat the coupon — no Discount-row credit.
    const base = saleAwareUnit * qty;
    return { base, charge: base, discount: 0, wonCopies: 0 };
  }

  return {
    base: listUnit * qty,
    charge: discountedListUnit * qty,
    discount: (listUnit - discountedListUnit) * qty,
    wonCopies: qty,
  };
}

export function computeCartTotals(
  items: any[],
  serialRowsByItem: SerialRowMap,
): {
  discountedLines: any[];
  baseTotal: number;
  percentDiscountTotal: number;
  dollarDiscount: number;
  totalDiscount: number;
  grandTotal: number;
  discountDescriptors: DiscountDescriptor[];
  saleBeatsAppliedDiscount: boolean;
} {
  const discountedLines = items.filter((it: any) => !!it.discountId);

  let baseTotal = 0;
  let percentDiscountTotal = 0;
  let dollarDiscount = 0;
  const savingsByItem = new Map<any, number>();
  const wonCopiesByItem = new Map<any, number>();

  // Order-scope trackers: one flat dollar amount per code; percent savings
  // aggregated per code into a single order-level row.
  const orderDollarByCode = new Map<string, { d: any; amount: number }>();
  const orderPercentByCode = new Map<string, { d: any; amount: number }>();

  items.forEach((it) => {
    if (isUpgradeItem(it)) {
      const rows = serialRowsByItem[cartItemKey(it)] || [];
      const saleSubtotal = rows.reduce((s, r) => {
        const hostApp = r.targetHostApp || r.hostApp || null;
        return s + getEffectivePrice(it.productId, hostApp);
      }, 0);
      baseTotal += saleSubtotal;
      return;
    }

    if (!it.discountId) {
      baseTotal +=
        getEffectivePrice(it.productId, it.hostApplication) * it.quantity;
      return;
    }

    const d = it.discountId;
    const codeKey = d._id?.toString?.() || d.code || 'discount';

    // Order-scope: no per-line cost breakdown — the discount hits the order
    // total. Lines contribute their regular (sale-aware) subtotal.
    if (isOrderScopeDiscount(d)) {
      baseTotal +=
        getEffectivePrice(it.productId, it.hostApplication) * it.quantity;

      if (d.amountUnits === 'dollars') {
        // Flat amount, applied ONCE per code regardless of how many lines
        // carry it.
        orderDollarByCode.set(codeKey, {
          d,
          amount: Number(d.amount) || 0,
        });
      } else if (d.amountUnits === 'percent') {
        const lineList =
          getBasePrice(it.productId, it.hostApplication) * it.quantity;
        const prev = orderPercentByCode.get(codeKey);
        orderPercentByCode.set(codeKey, {
          d,
          amount: (prev?.amount || 0) + lineList * (Number(d.amount) / 100),
        });
      }
      return;
    }

    const { base, discount, wonCopies } = breakdownDiscountedLine(it);
    baseTotal += base;
    savingsByItem.set(it, discount);
    wonCopiesByItem.set(it, wonCopies);

    if (d.amountUnits === 'percent') {
      percentDiscountTotal += discount;
    } else if (d.amountUnits === 'dollars') {
      dollarDiscount += discount;
    }
  });

  // Upgrade rows: percent of sale-aware serial row prices (legacy).
  const upgradeSavingsByItem = new Map<any, number>();
  discountedLines.forEach((it) => {
    const d = it.discountId;
    if (!d || !isUpgradeItem(it)) return;
    if (d.amountUnits !== 'percent') return;

    const rows = serialRowsByItem[cartItemKey(it)] || [];
    const subtotal = rows.reduce((s, r) => {
      const hostApp = r.targetHostApp || r.hostApp || null;
      return s + getEffectivePrice(it.productId, hostApp);
    }, 0);
    const savings = subtotal * (Number(d.amount) / 100);
    upgradeSavingsByItem.set(it, savings);
    percentDiscountTotal += savings;
  });

  // Order-scope totals fold in once per code.
  orderDollarByCode.forEach(({ amount }) => {
    dollarDiscount += amount;
  });
  orderPercentByCode.forEach(({ amount }) => {
    percentDiscountTotal += amount;
  });

  dollarDiscount = Math.min(dollarDiscount, baseTotal);
  const totalDiscount = Math.min(
    percentDiscountTotal + dollarDiscount,
    baseTotal,
  );
  const grandTotal = Math.max(0, baseTotal - totalDiscount);

  // Code stamped, but every discounted copy lost choose-min to sale.
  const saleBeatsAppliedDiscount =
    discountedLines.length > 0 &&
    totalDiscount <= 0 &&
    discountedLines.some((it) => {
      if (isUpgradeItem(it)) return false;
      const list = getBasePrice(it.productId, it.hostApplication);
      const saleAware = getEffectivePrice(it.productId, it.hostApplication);
      return saleAware < list;
    });

  // Item-scope: one row per line. Order-scope: exactly one row per code —
  // "the total discount applied to the order", no per-item breakdown.
  const discountDescriptors: DiscountDescriptor[] = discountedLines
    .filter((it: any) => !isOrderScopeDiscount(it.discountId))
    .map((it: any, i: number) => {
      const d = it.discountId;
      const name = it.productId?.name || 'item';
      let text: string | undefined;
      let amount: number | undefined;

      if (isUpgradeItem(it) && d) {
        const savings = upgradeSavingsByItem.get(it) || 0;
        if (savings <= 0) return null;
        if (d.amountUnits === 'percent') {
          text = `${Number(d.amount)}% off ${name}`;
        }
        amount = savings;
      } else if (d) {
        const savings = savingsByItem.get(it) || 0;
        const won = wonCopiesByItem.get(it) || 0;
        if (savings <= 0 || won <= 0) return null;

        if (d.amountUnits === 'percent') {
          text =
            won > 1
              ? `${Number(d.amount)}% off ${name} x${won}`
              : `${Number(d.amount)}% off ${name}`;
        } else if (d.amountUnits === 'dollars') {
          const unit = Number(d.amount) || 0;
          text =
            won > 1
              ? `$${unit.toFixed(2)} off ${name} x${won}`
              : `$${unit.toFixed(2)} off ${name}`;
        }
        amount = savings;
      }

      if (!text) return null;
      return { key: `${cartItemKey(it) || i}`, text, amount };
    })
    .filter(Boolean) as DiscountDescriptor[];

  orderDollarByCode.forEach(({ d, amount }, codeKey) => {
    if (amount <= 0) return;
    discountDescriptors.push({
      key: `order-${codeKey}`,
      text: `$${amount.toFixed(2)} off order`,
      amount,
    });
  });
  orderPercentByCode.forEach(({ d, amount }, codeKey) => {
    if (amount <= 0) return;
    discountDescriptors.push({
      key: `order-${codeKey}`,
      text: `${Number(d.amount)}% off order`,
      amount,
    });
  });

  return {
    discountedLines,
    baseTotal,
    percentDiscountTotal,
    dollarDiscount,
    totalDiscount,
    grandTotal,
    discountDescriptors,
    saleBeatsAppliedDiscount,
  };
}

/** Customer-facing line total (order-scope discounts never change the line). */
export function getLineDisplayTotal(
  item: any,
  serialRowsByItem: SerialRowMap,
): number {
  if (isUpgradeItem(item)) {
    const rows = serialRowsByItem[cartItemKey(item)] || [];
    return rows.reduce((sum, row) => {
      const hostApp = row.targetHostApp || row.hostApp || null;
      return sum + getEffectivePrice(item.productId, hostApp);
    }, 0);
  }

  if (item.discountId && !isOrderScopeDiscount(item.discountId)) {
    return breakdownDiscountedLine(item).charge;
  }

  return (
    getEffectivePrice(item.productId, item.hostApplication) * item.quantity
  );
}
